import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import TitleLogo from 'components/TitleLogo';
import BadgeLabel from 'components/BadgeLabel';
import { VideoCard } from 'components/VerticalVideo/VideoCard';
import { NotchedHorizontalRule } from 'components/NotchedHorizontalRule';
import styles from './styles.module.scss';

const CARD_WIDTH = 178;
const CARD_GAP = 10;
const FULL_CARD_WIDTH = CARD_WIDTH + CARD_GAP;

/**
 *
 * @param {object} props
 * @param {object} props.content
 * @param {( video: object ) => void} props.videoCardClicked Callback that video card was clicked
 */
export const VideoCardCarousel = ({ content, videoCardClicked }) => {
  const scrollRef = useRef(null);
  const [showPrev, setShowPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const {
    items: unfilteredItems,
    metadata: {
      title,
      packageHeadline,
      pkgUrl = '',
      eyebrow,
      hasEyebrow,
      hasPackageHeadline,
      logoUrl,
    },
  } = content;
  const items = unfilteredItems?.filter(
    (i) => Boolean(i) && (i.type !== 'product' || (Boolean(i.id) && Boolean(i.item))),
  );
  const allIds = items.map((i) => i?.item?.id ?? i.id);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    /**
     *
     */
    const checkScroll = () => {
      const hasOverflow = node.scrollWidth > node.clientWidth + 1;
      setCanScrollNext(hasOverflow);
    };
    checkScroll();

    window.addEventListener('resize', checkScroll);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('resize', checkScroll);
  }, [items]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    /**
     * function that determines whether we show the previous card
     */
    const onScroll = () => {
      setShowPrev(node.scrollLeft > FULL_CARD_WIDTH / 2);
    };

    node.addEventListener('scroll', onScroll, { passive: true });
    // eslint-disable-next-line consistent-return
    return () => node.removeEventListener('scroll', onScroll);
  });

  const packageHeadlineContainer = hasPackageHeadline && (
    <div data-testid="verticalvideo-headline" className={styles.pkgHeadlineContainer}>
      <div className={styles.pkgHeadline}>
        {pkgUrl ? <a href={pkgUrl}>{packageHeadline}</a> : <>{packageHeadline}</>}
      </div>
    </div>
  );

  const imgRender = (
    <img
      data-testid="vertvideo__logo"
      alt="Logo for Subnav"
      className={styles.titleImage}
      src={logoUrl}
    />
  );

  const textRender = (
    <h2 className={styles.vertVideoTitle} data-testid="vertvideo__headline">
      <TitleLogo title={title} logoUrl={logoUrl} />
    </h2>
  );

  const titleRender = logoUrl ? imgRender : textRender;

  const renderTitleContainer = title && (
    <div className={styles.vertVideoHeader}>
      <div className={styles.vertVideoHeaderheadline}>
        {pkgUrl && !logoUrl ? <a href={pkgUrl}>{titleRender}</a> : <>{titleRender}</>}
      </div>
    </div>
  );

  const eyebrowContainer = hasEyebrow && hasPackageHeadline && (
    <div data-testid="verticalvideo-eyebrow" className={styles.eyebrowContainer}>
      {pkgUrl
        ? (
          <a href={pkgUrl}>
            <BadgeLabel eyebrowText={eyebrow} badgeType="EYEBROW" />
          </a>
        )
        : <BadgeLabel eyebrowText={eyebrow} badgeType="EYEBROW" />}
    </div>
  );

  /**
   *
   * @param {number} amount
   */
  const scrollTo = (amount) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  /**
   * Scrolls to the next set of video cards.
   */
  const scrollToNext = () => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 10;

    if (atEnd) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: FULL_CARD_WIDTH * 2, behavior: 'smooth' });
    }
  };

  /**
   * Scrolls to the previous set of video cards.
   */
  const scrollToPrev = () => {
    const fullWidth = CARD_WIDTH + CARD_GAP;
    scrollTo(-fullWidth * 2);
  };

  /**
   * Renders the previous button if there are more items to scroll back to.
   */
  const renderPrevBtn = () => {
    if (!showPrev) return null;
    return (
      <div
        className={classNames(
          styles.vertVideoPrevBtnContainer,
          showPrev && styles.vertVideoPrevBtnActive,
        )}
        data-testid="vertvideo__page-btns__prev-container"
      >
        <button
          onClick={scrollToPrev}
          type="button"
          className={styles.vertVideoPageBtnPrev}
          data-testid="vertvideo__page-btns__prev"
        >
          <span className={classNames(styles.vertVideoPageBtnIcon, 'icon icon-angle-down')} />
        </button>
      </div>
    );
  };

  /**
     * Renders the next button if there are more items to scroll to.
     */
  const renderNextBtn = () => {
    if (!canScrollNext) return null;
    return (
      <div
        className={styles.vertVideoNextBtnContainer}
        data-testid="vertvideo__page-btns__next-container"
      >
        <button
          onClick={scrollToNext}
          type="button"
          className={styles.vertVideoPageBtnNext}
          data-testid="vertvideo__page-btns__next"
        >
          <span className={classNames(styles.vertVideoPageBtnIcon, 'icon icon-angle-down')} />
        </button>
      </div>
    );
  };
  return (
    <div data-testid="video-card-carousel">
      <div className={styles.vertVideoHeadlineContainer}>
        <NotchedHorizontalRule
          className={styles.vertVideoHeaderTopBorder}
          data-testid="vertvideo__header__top-border"
        />
        {renderTitleContainer}
        {eyebrowContainer}
        {packageHeadlineContainer}
      </div>
      <div className={styles.vertVideoScrollWrapper}>
        {renderPrevBtn()}
        <div ref={scrollRef} className={styles.vertVideoScrollContainer} data-testid="vertvideo-scroll-container">
          {items.map((itemObj, index) => {
            if (!itemObj || !itemObj?.computedValues) return null;
            const indexOffset = index + 1;
            const keyVal = itemObj?.item?.id ?? itemObj.id;
            return (
              <div data-testid="card" key={keyVal} className={styles.vertVideoCardWrapper}>
                <VideoCard
                  packageVideoIds={allIds}
                  item={itemObj}
                  indexOffset={indexOffset}
                  onSelect={() => {
                    videoCardClicked(index);
                  }}
                />
              </div>
            );
          })}
        </div>
        {renderNextBtn()}
      </div>
    </div>
  );
};
